import { Association, Opportunity, OpportunityParticipant } from "@entities";
import { Roles } from "@shared/Roles";
import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { clampMatchWeight } from "@shared/helpers/match-weight";
import { syncOpportunityFullFlagFromSeats } from "@shared/helpers/sync-opportunity-full-flag";
import type { EntityFilter, MembersOnly } from "remult";
import { Controller, remult, repo } from "remult";

@Controller("volunteers-table")
export class VolunteersTableController {
  /**
   * Save teams for associations matching the given filter.
   * @param where - EntityFilter to specify which associations to update.
   * @param teams - Array of team IDs to associate with the filtered associations.
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async saveTeams(where: EntityFilter<Association>, teams: string[]) {
    try {
      const associations = await repo(Association).find({
        where,
        include: { teams: true },
      });
      await Promise.all(
        associations.map(async (association) => {
          const relations = repo(Association).relations(association);
          await relations.teams.insert(
            teams
              .filter(
                (team) =>
                  !association.teams.some(
                    (alreadyTeam) => alreadyTeam.teamId === team,
                  ),
              )
              .map((team) => ({ teamId: team })),
          );
        }),
      );
    } catch (error) {
      console.error("Error saving teams:", error);
      throw new Error("Failed to save teams. Please try again later.");
    }
  }

  /**
   * Bulk-assign selected opportunities to filtered associations (admin only).
   * New participant rows are created with `registeredManually: true`.
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async handleOpportunitiesSelect(
    selected: string[],
    where: EntityFilter<Association>,
  ) {
    try {
      const associations = await repo(Association).find({
        where,
        include: { user: true },
      });

      if (associations.length === 0 || selected.length === 0) {
        return;
      }

      const associationIds = associations.map((a) => a.id);
      const managerId = remult.user?.id;
      const managerMatchAttribution = managerId
        ? {
            matchedByManagerId: managerId,
            matchedAt: new Date(),
          }
        : {};

      const [alreadyParticipants, opportunityById] = await Promise.all([
        repo(OpportunityParticipant).find({
          where: {
            association: { $id: { $in: associationIds } },
            opportunity: { $id: { $in: selected } },
          },
          include: { association: true, opportunity: true },
        }),
        opportunityMapFromIds(selected),
      ]);

      const pairKey = (associationId: string, opportunityId: string) =>
        `${associationId}:${opportunityId}`;

      const existing = new Map(
        alreadyParticipants.map((p) => [
          pairKey(p.association!.id, p.opportunity!.id),
          p,
        ]),
      );

      const newParticipants: Partial<MembersOnly<OpportunityParticipant>>[] =
        [];
      const reactivateUpdates: Promise<unknown>[] = [];

      for (const association of associations) {
        for (const opportunityId of selected) {
          const key = pairKey(association.id, opportunityId);
          const existingParticipant = existing.get(key);
          if (existingParticipant) {
            if (
              existingParticipant.status === "DECLINED" ||
              existingParticipant.status === "PENDING"
            ) {
              const opportunity = opportunityById.get(opportunityId);
              reactivateUpdates.push(
                repo(OpportunityParticipant).update(existingParticipant.id, {
                  status: "APPROVED",
                  registeredManually: true,
                  ...managerMatchAttribution,
                  groupId: "",
                  matchWeight: clampMatchWeight(opportunity?.defaultMatchWeight),
                }),
              );
            }
            continue;
          }
          newParticipants.push({
            association,
            opportunity: opportunityById.get(opportunityId),
            status: "APPROVED",
            registeredManually: true,
            ...managerMatchAttribution,
            matchWeight: clampMatchWeight(
              opportunityById.get(opportunityId)?.defaultMatchWeight,
            ),
          });
        }
      }

      await Promise.all(reactivateUpdates);

      if (newParticipants.length > 0) {
        await repo(OpportunityParticipant).insert(newParticipants);
      }

      for (const opportunityId of selected) {
        const opportunity = opportunityById.get(opportunityId);
        if (!opportunity) continue;
        await syncOpportunityFullFlagFromSeats(opportunity, false);
      }
    } catch (error) {
      console.error("Error handling opportunities selection:", error);
      throw new Error(
        "Failed to handle opportunities selection. Please try again later.",
      );
    }
  }
}

async function opportunityMapFromIds(
  ids: string[],
): Promise<Map<string, Opportunity>> {
  const entries = await Promise.all(
    ids.map(async (id) => [id, await repo(Opportunity).findId(id)] as const),
  );
  const map = new Map<string, Opportunity>();
  for (const [id, o] of entries) {
    if (o) map.set(id, o);
  }
  return map;
}
